import { Table, Space, Button, Tag } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { StatusBadge } from './StatusBadge';
import { formatDate, formatCurrency, formatPhone } from '../utils/formatters';
import { CHANNEL_LABELS, CHANNEL_COLORS } from '../utils/constants';
import type { Order } from '../types';

interface OrderTableProps {
  orders: Order[];
  loading?: boolean;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };
}

export const OrderTable: React.FC<OrderTableProps> = ({
  orders,
  loading,
  pagination
}) => {
  const navigate = useNavigate();

  const columns: ColumnsType<Order> = [
    {
      title: 'ID',
      dataIndex: 'order_id',
      key: 'order_id',
      width: 170,
      render: (text: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>{text}</span>
      )
    },
    {
      title: 'Клієнт',
      key: 'client',
      width: 190,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span style={{ whiteSpace: 'nowrap' }}>{record.client_name || '—'}</span>
          <span style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>
            {formatPhone(record.client_phone)}
          </span>
        </Space>
      )
    },
    {
      title: 'Канал',
      dataIndex: 'channel',
      key: 'channel',
      width: 110,
      render: (channel: keyof typeof CHANNEL_LABELS) =>
        channel ? (
          <Tag color={CHANNEL_COLORS[channel]}>
            {CHANNEL_LABELS[channel] || channel}
          </Tag>
        ) : '—'
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status) => <StatusBadge status={status} />
    },
    {
      title: 'Сума',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      render: (amount: number | null) => <strong>{formatCurrency(amount)}</strong>
    },
    {
      title: 'Створено',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (date: string) => formatDate(date)
    },
    {
      title: 'Дії',
      key: 'actions',
      width: 105,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/orders/${record.order_id}`)}
        >
          Деталі
        </Button>
      )
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={orders}
      loading={loading}
      rowKey="order_id"
      pagination={pagination}
      scroll={{ x: 900 }}
    />
  );
};
